import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, ArrowLeft, RotateCcw } from 'lucide-react';
import toast from '../utils/toast';
import { isValidPhoneNumber, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton, masterHeaderCard, masterHeaderIcon, masterInput, masterInputLabel,
  masterPageShell, masterToolbar, masterPanelPadding, masterPrimaryButton,
  masterSearchInput, masterSearchWrap, masterSelect, masterSubtitle, masterTitle,masterTableData
} from '../components/masterPageStyles';

const emptyForm = { companyId: '', companyName: '', contactName: '', contactNumber: '', status: 'Active' };

const CompanyName = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/master/company/all');
      if (res.data.success) setItems(res.data.data || []);
    } catch (err) { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };

  const fetchNextId = async () => {
    try {
      const res = await api.get('/master/company/next-id');
      if (res.data?.success) setFormData((p) => ({ ...p, companyId: res.data.nextId }));
    } catch (_) {}
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...items].sort((a, b) => {
      const idA = String(a.companyId || a._id || '');
      const idB = String(b.companyId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const companyId = (item.companyId || '').toLowerCase();
      const companyName = (item.companyName || '').toLowerCase();
      const contactName = (item.contactName || '').toLowerCase();
      const contactNumber = (item.contactNumber || '').toLowerCase();

      return (
        companyId.startsWith(q) ||
        companyName.startsWith(q) ||
        contactName.startsWith(q) ||
        contactNumber.startsWith(q)
      );
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({ ...item });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'contactNumber') {
      setFormData(prev => ({ ...prev, contactNumber: normalizePhoneInput(value) }));
      return;
    }
    if (name === 'status') {
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.contactNumber && !isValidPhoneNumber(formData.contactNumber)) {
      return toast.error('Contact number must be exactly 10 digits');
    }
    setIsSaving(true);
    try {
      await api.post('/master/company/save', {
        ...formData,
        id: editId,
        companyName: formData.companyName.trim().toUpperCase(),
        contactName: String(formData.contactName || '').trim().toUpperCase()
      });
      toast.success(editId ? 'Updated' : 'Saved');
      setEditId(null);
      setFormData(emptyForm);
      setShowForm(true);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setIsSaving(false); }
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Layers size={10} /></div>
          <div><h4 className={masterTitle}>Company Master</h4>
          <p className={masterSubtitle}>Manufacturer Setup</p>
          </div>
        </div>
        {!showForm ? (
          <button onClick={async () => { setEditId(null); setFormData(emptyForm); setShowForm(true); await fetchNextId(); }} className={masterAccentButton}><Plus size={14} /> Add Company</button>
        ) : (
          <button onClick={() => setShowForm(false)} className={masterPrimaryButton}>
            <ArrowLeft size={14} /> VIEW LIST
         </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h4 className="text-sm font-bold text-slate-800 uppercase">{editId ? 'Edit Company' : 'New Company'}</h4>
              <button type="button" onClick={() => { setEditId(null); setFormData(emptyForm); }} className="text-[10px] font-bold text-rose-500 uppercase flex items-center gap-1 hover:underline"><RotateCcw size={12}/> Reset</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-1 gap-y-1">
              {/* <Input label="Company ID" name="companyId" value={formData.companyId} readOnly /> */}
              <Input label="Company Name" name="companyName" value={formData.companyName} onChange={handleChange} required />
              <Input label="Contact Name" name="contactName" value={formData.contactName || ''} onChange={handleChange} />
              <Input label="Contact Number" name="contactNumber" value={formData.contactNumber || ''} onChange={handleChange} inputMode="numeric" maxLength={10} />
              <Select label="Status" name="status" value={formData.status} onChange={handleChange} options={['Active', 'Inactive']} />
            </div>
            <div className="pt-1 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {isSaving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} {editId ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
            <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by company ID, name, contact name, or phone..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>
            
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData}>Company Name</th>
                    <th className={masterTableData}>Contact Name</th>
                    <th className={masterTableData}>Contact Number</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {loading ? (<tr><td colSpan="7" className="p-12 text-center">Syncing...</td></tr>) : 
                  paginatedData.map((item, index) => (
                    <tr key={item._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}>{index + 1 + (currentPage - 1) * pageSize}</td>

                      <td className="p-1 border border-slate-400 text-center text-emerald-600">{item.companyId}</td>

                      <td className={masterTableData}>{item.companyName}</td>
                      <td className={masterTableData}>{item.contactName || '-'}</td>
                      <td className={masterTableData}>{item.contactNumber || '-'}</td>

                      <td className={masterTableData}><span className={`px-2 rounded text-[9px] ${item.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>{item.status}</span></td>

                      <td className={masterTableData}><div className="flex justify-center gap-1">
                        <button onClick={() => handleEdit(item)} className="p-1 text-emerald-600 rounded-lg hover:bg-emerald-100"><Edit size={14} /></button>
                        <button onClick={async () => { if(window.confirm('Delete?')) { await api.delete(`/master/company/${item._id}`); fetchData(); } }} className="p-1 text-rose-600 rounded-lg hover:bg-rose-100"><Trash2 size={14} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
    </div>
);
const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

export default CompanyName;
